import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { useLocalStorage } from '@/hooks/browser/use-local-storage';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';

export enum GlobalCSSVariable {
  MainNavWidth = '--hydra-main-nav-width',
  MainNavTransitionTime = '--hydra-main-nav-transition-time', // set in index.scss, not usually set dynamically
  MainNavTransitionCurve = '--hydra-main-nav-transition-curve', // set in index.scss, not usually set dynamically
  TopNavHeight = '--hydra-top-nav-height',
  WorkspaceBorderHeight = '--hydra-workspace-border-height',
  WorkspaceBorderColor = '--hydra-workspace-border-color',
}

export interface IThemeContext {
  isDarkMode: boolean;
  setIsDarkMode: (isDarkMode: boolean) => void;
  toggleDarkMode: () => void;
  setVariable: (variable: GlobalCSSVariable, value: string) => void;
  setVariables: (variables: Partial<Record<GlobalCSSVariable, string>>) => void;
  getVariable: (variable: GlobalCSSVariable) => string;
  removeVariable: (variable: GlobalCSSVariable) => void;
}

export const ThemeContext = createContext<IThemeContext | null>(null);

export function useThemeContext() {
  const context = useContext(ThemeContext);
  if (context == null) {
    throw new Error('useThemeContext must be used within a ThemeContext');
  }
  return context;
}

export function ThemeContextProvider({ children }: { children: ReactNode }) {
  const isDarkModeToggleEnabled = useFeatureFlag(FeatureFlag.DarkModeToggle);
  const [isDarkMode, setIsDarkMode] = useLocalStorage<boolean>('dark-mode-enabled', false);

  const toggleDarkMode = () => {
    setIsDarkMode((current) => !current);
  };

  const setVariable = (variable: GlobalCSSVariable, value: string) => {
    document.documentElement.style.setProperty(variable, value);
  };

  const setVariables = (variables: Partial<Record<GlobalCSSVariable, string>>) => {
    for (const [variable, value] of Object.entries(variables)) {
      setVariable(variable as GlobalCSSVariable, value);
    }
  };

  const getVariable = (variable: GlobalCSSVariable) => {
    return document.documentElement.style.getPropertyValue(variable);
  };

  const removeVariable = (variable: GlobalCSSVariable) => {
    document.documentElement.style.removeProperty(variable);
  };

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode: isDarkModeToggleEnabled ? isDarkMode : false,
        setIsDarkMode,
        toggleDarkMode,
        setVariable,
        setVariables,
        getVariable,
        removeVariable,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
